"use client"
import { Award, Clock, PoundSterling } from "lucide-react";
import Image from "next/image";
import HeroImage from "@images/landing/hero.webp"
import { Marquee } from "@/components/ui/marquee";
import MultistepForm from "@/components/valuation-form";
import { useEffect } from "react";
import { scrollToElement } from "@/lib/scroll-utils";
import { SectionTitle } from "@/components/ui/section-title";

const watchBrands = [
    {
        name: "Rolex",
        logo: "/images/landing/logos/rolex.svg",
        height: 72
    },
    {
        name: "Patek Philippe",
        logo: "/images/landing/logos/patek.svg",
        height: 90
    },
    {
        name: "Audemars Piguet",
        logo: "/images/landing/logos/audemars.svg",
        height: 84
    },
    {
        name: "Omega",
        logo: "/images/landing/logos/omega.svg",
        height: 64
    },
    {
        name: "Richard Mille",
        logo: "/images/landing/logos/rm.svg",
        height: 56
    },

     {
        name: "Cartier",
        logo: "/images/landing/logos/cartier.svg",
        height: 56
    },
     {
        name: "Tudor",
        logo: "/images/landing/logos/tudor.svg",
        height: 56
    }

];

export default function Hero(){
    useEffect(() => {
        // Check if there's a hash in the URL and scroll to it
        if (window.location.hash === '#sell-watch-form') {
            // Small delay to ensure the component is fully rendered
            setTimeout(() => {
                scrollToElement('sell-watch-form', 80);
            }, 100);
        }
    }, []);

    return(
        <section id="home" className="wrapper pt-medium overflow-hidden">
            <div className="flex flex-col w-full max-w-screen-2xl h-fit pt-medium pb-8">

                <div className="flex flex-col lg:flex-row items-start sm:items-center justify-between gap-8 xl:gap-16"> 
                    
                    {/** Text Content */}
                    <div className="flex flex-col items-start sm:items-center lg:items-start w-full lg:w-3/5">
                        <SectionTitle theme="dark">
                            Get Your Free Quote Today
                        </SectionTitle>
                        <h1 className="text-h1 text-left sm:text-center lg:text-left text-balance">
                            Turn Your Luxury Watch Into Cash Today
                        </h1>
                        <div className="flex flex-wrap gap-2 lg:gap-4 justify-start mt-4 order-1 lg:order-none">
                            {sellingPoints.map((point, index) => {
                                const IconComponent = point.icon;
                                return (
                                    <div key={index} className={`flex items-center gap-2 h-8 ${index >= 2 ? 'hidden xl:flex' : ''}`}>
                                        <IconComponent 
                                            className="size-4 lg:size-6 text-cta flex-shrink-0" 
                                        />
                                        <span className="text-small font-medium">
                                            {point.text}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                        
                        <p className="text-left sm:text-center lg:text-left text-balance text-h6 mt-6 lg:max-w-2xl">
                            We offer competitive prices for Rolex, Patek Philippe, Omega, and other premium timepieces with fast quotes and same-day payment.
                        </p>
                    </div>
                                    
                    {/** Form */}
                    <div className="flex justify-start sm:justify-center lg:justify-start w-full lg:w-2/5 lg:max-w-md lg:translate-y-16 lg:pr-8">
 
                        <div id="sell-watch-form" className="w-full">
                            <div className="lg:h-[460px] flex flex-col items-center justify-start w-full">
                                <MultistepForm dialog={true} />
                            </div>
                        </div>
                    </div>
                        
                </div>
                <div className="mt-8 hidden lg:flex lg:-mt-0 xl:-mt-8">
                    <Image src={HeroImage} alt="" className="aspect-[16/5] object-cover rounded-t-3xl rounded-b-xl"/>
                </div>
                
                <div className="flex flex-col lg:flex-row items-center justify-between gap-2 lg:gap-16 mt-12">
                    <p className="text-h6 md:text-h5 lg:text-h3 lg:max-w-sm text-balance text-muted-foreground text-center md:text-left  lg:border-r border-foreground/50 pr-0 lg:pr-8">
                        We buy from these selected premium brands
                    </p>

                    <Marquee
                        pauseOnHover
                        className="[--duration:30s] [mask-image:linear-gradient(to_right,transparent_0%,#000_20%,#000_80%,transparent_100%)]  lg:max-w-screen-md xl:max-w-screen-lg"
                    >
                        {watchBrands.map((brand, idx) => (
                            <div
                                key={idx}
                                className="flex items-center justify-center px-2 md:px-8 py-4"
                            >
                                <Image
                                    src={brand.logo}
                                    alt={`${brand.name} logo`}
                                    width={brand.height * 2}
                                    height={brand.height}
                                    className="object-contain opacity-90 hover:opacity-100 transition-opacity duration-300"
                                    style={{ height: `${brand.height}px` }}
                                />
                            </div>
                        ))}
                    </Marquee>
                </div>
            </div>

        </section>
    )
}

const sellingPoints = [
        
        {
            icon: Award,
            text: "15+ Years Experience"
        },
        {
            icon: Clock,
            text: "Same Day Payments"
        },
        
        {
            icon: PoundSterling,
            text: "Competitive & Fair Prices"
        },
]